/** Shared "where is this used" (cross-reference) envelope, reused by every
 *  satellite catalog editor (regions, trip types, …).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet } from './client';

/** A single referencing object. `dashboard_link`/`admin_link` are absolute
 *  URLs when the object has a canonical place in the dashboard / Django admin. */
export interface UsedByRef {
  type_name: string;
  type_key: string;
  id: number;
  description: string;
  dashboard_link: string | null;
  admin_link: string | null;
}

export interface UsedByTypeOption {
  key: string;
  name: string;
  /** The type's total rows before `search` / `type` (sent by the grouped used-bys, e.g.
   *  activities'). */
  count?: number;
  /** A type that is counted, never listed: it has no rows, is not a `type` filter value and
   *  adds nothing to the envelope's `count` (activities' "Unlisted Trip"). */
  count_only?: boolean;
  /** Count-only types: more rows than `count` (the backend's cap) — read as "1,000+". */
  capped?: boolean;
}

export interface UsedByResponse {
  count: number;
  results: UsedByRef[];
  available_types: UsedByTypeOption[];
}

/** The catalog resources that expose a `/{resource}/{id}/used_by/` endpoint. */
export type UsedByResource =
  | 'regions'
  | 'trip_types'
  | 'pre_trip_items'
  | 'pre_trip_item_types'
  | 'activities'
  | 'activity_categories'
  | 'route_stops'
  | 'route_sets'
  | 'itineraries'
  | 'notification_rulesets'
  | 'api_credentials';

export interface UsedByParams {
  type?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

/** `T` narrows the envelope for a resource whose rows carry more (the activity and itinerary
 *  used-bys: `api/activities.ts` `usedBy`, `api/itineraries.ts` `usedBy`). */
export function fetchUsedBy<T extends UsedByResponse = UsedByResponse>(
  resource: UsedByResource,
  id: number,
  params: UsedByParams = {},
): Promise<T> {
  const usp = new URLSearchParams();
  if (params.type) usp.set('type', params.type);
  if (params.search) usp.set('search', params.search);
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  const qs = usp.toString();
  return apiGet<T>(`/${resource}/${id}/used_by/${qs ? `?${qs}` : ''}`);
}
